import React, { useState, useEffect, useRef } from 'react';
// Header is provided by LayoutWithHeader in App.js
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

// Fix for default marker icons
if (L.Icon.Default && L.Icon.Default.mergeOptions) {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    });
}

function parseAddress(address) {
    if (!address) return { country: '', city: '', neighborhood: '', street: '', house: '' };
    const parts = address.split(',').map(s => s.trim());
    return {
        country: parts[parts.length - 1] || '',
        city: parts.length > 2 ? parts[parts.length - 3] : '',
        neighborhood: '',
        street: parts.length > 1 ? parts[parts.length - 2] : '',
        house: ''
    };
}

export default function HostDinnerAddress() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state || {};
    const { address: passedAddress, coords: passedCoords } = state;
    const parsed = parseAddress(passedAddress);

    const [form, setForm] = useState({
        country: parsed.country || '',
        city: parsed.city || '',
        neighborhood: '',
        street: parsed.street || '',
        house: '',
    });
    const [coords, setCoords] = useState(passedCoords || [31.7683, 35.2137]);
    const [errors, setErrors] = useState({});
    const [suggestions, setSuggestions] = useState([]);
    const [activeField, setActiveField] = useState('');
    const [addressVerified, setAddressVerified] = useState(false);
    const autocompleteTimeout = useRef();

    // Autocomplete handler
    const handleAutocomplete = (field, value) => {
        console.log(`handleAutocomplete: field=${field}, value=${value}`);
        setForm(prev => ({ ...prev, [field]: value }));
        setActiveField(field);
        setSuggestions([]);
        setAddressVerified(false);
        if (autocompleteTimeout.current) clearTimeout(autocompleteTimeout.current);

        if (value.length < 2) return;

        autocompleteTimeout.current = setTimeout(async () => {
            let queryParams = new URLSearchParams({ format: 'json', limit: 10 });
            let specificQuery = '';

            if (field === 'country') {
                queryParams.append('country', value);
                queryParams.append('featuretype', 'country');
            } else if (field === 'city') {
                if (!form.country) return;
                queryParams.append('city', value);
                queryParams.append('country', form.country);
            } else if (field === 'neighborhood') {
                if (!form.city) return;
                queryParams.append('q', `${value}, ${form.city}, ${form.country}`);
                queryParams.append('class', 'place');
                queryParams.append('extratags', '1');
            } else if (field === 'street') {
                if (!form.city) return;
                specificQuery = `${value}, ${form.city}, ${form.country}`;
                if (form.neighborhood) {
                    specificQuery = `${value}, ${form.neighborhood}, ${form.city}, ${form.country}`;
                }
                queryParams.append('q', specificQuery);
                queryParams.append('class', 'highway');
            } else {
                return;
            }

            try {
                const url = `https://nominatim.openstreetmap.org/search?${queryParams.toString()}`;
                console.log('Fetching URL:', url);
                const res = await fetch(url);
                const data = await res.json();
                console.log('Autocomplete API response:', data);

                let filteredData = data;
                if (field === 'country') {
                    filteredData = data.filter(s => s.class === 'place' && s.type === 'country');
                } else if (field === 'city') {
                    filteredData = data.filter(s => s.class === 'place' && s.type === 'city');
                } else if (field === 'neighborhood') {
                    filteredData = data.filter(s =>
                        (s.class === 'place' && (s.type === 'suburb' || s.type === 'quarter' || s.type === 'neighbourhood' || s.type === 'hamlet' || s.type === 'village')) ||
                        (s.address && (s.address.suburb || s.address.quarter || s.address.neighbourhood))
                    );
                } else if (field === 'street') {
                    filteredData = data.filter(s => s.class === 'highway' || (s.type && s.type.includes('street')));
                }

                setSuggestions(filteredData);

                // Update map with first result
                if (filteredData.length > 0) {
                    const firstResult = filteredData[0];
                    const newCoords = [parseFloat(firstResult.lat), parseFloat(firstResult.lon)];
                    setCoords(newCoords);
                    console.log('Map coords updated to (from autocomplete):', newCoords);
                }
            } catch (error) {
                console.error('Autocomplete error:', error);
                setSuggestions([]);
            }
        }, 300);
    };

    // When a suggestion is selected
    const handleSuggestionClick = (s) => {
        console.log('Suggestion clicked:', s);
        let update = {};
        const suggestedName = s.name || s.display_name.split(',')[0].trim();

        if (activeField === 'country') {
            update = { country: suggestedName };
            const newCoords = [parseFloat(s.lat), parseFloat(s.lon)];
            setCoords(newCoords);
            console.log('Map coords updated to (from suggestion - country):', newCoords);
        } else if (activeField === 'city') {
            update = { city: suggestedName };
            const newCoords = [parseFloat(s.lat), parseFloat(s.lon)];
            setCoords(newCoords);
            console.log('Map coords updated to (from suggestion - city):', newCoords);
        } else if (activeField === 'neighborhood') {
            update = { neighborhood: suggestedName };
            const newCoords = [parseFloat(s.lat), parseFloat(s.lon)];
            setCoords(newCoords);
            console.log('Map coords updated to (from suggestion - neighborhood):', newCoords);
        } else if (activeField === 'street') {
            update = { street: suggestedName };
            const newCoords = [parseFloat(s.lat), parseFloat(s.lon)];
            setCoords(newCoords);
            console.log('Map coords updated to (from suggestion - street):', newCoords);
        }

        setForm(prev => ({ ...prev, ...update }));
        setSuggestions([]);
        setActiveField('');
        setAddressVerified(false);
    };

    // Verify address and update map
    const verifyAndSetCoords = async () => {
        console.log('Verifying address...');
        setAddressVerified(false);
        setErrors({});
        const { country, city, street, house } = form;
        if (!country || !city || !street || !house) {
            setErrors(prev => ({ ...prev, address: 'Please fill all required address fields.' }));
            console.log('Verification skipped: Missing required fields.');
            return;
        }
        const query = `${house} ${street}, ${city}, ${country}`;
        console.log('Verification query:', query);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`);
            const data = await res.json();
            console.log('Verification API response:', data);
            if (data && data.length > 0) {
                const newCoords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
                setCoords(newCoords);
                setAddressVerified(true);
                console.log('Address verified! Map coords updated to:', newCoords);
            } else {
                setAddressVerified(false);
                setErrors(prev => ({ ...prev, address: 'Address not found. Please check your details.' }));
                console.log('Address not found during verification.');
            }
        } catch (error) {
            setAddressVerified(false);
            setErrors(prev => ({ ...prev, address: 'Error verifying address.' }));
            console.error('Error during address verification:', error);
        }
    };

    // Re-verify address when relevant fields change
    useEffect(() => {
        console.log('Form data changed, checking for verification trigger:', form);
        if (form.country && form.city && form.street && form.house) {
            verifyAndSetCoords();
        } else {
            setAddressVerified(false);
        }
    }, [form.country, form.city, form.street, form.house]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setAddressVerified(false);
        setSuggestions([]);
        console.log(`handleChange: ${name}=${value}`);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Submit button clicked.');
        const newErrors = {};
        if (!form.country) newErrors.country = 'Country is required';
        if (!form.city) newErrors.city = 'City is required';
        if (!form.street) newErrors.street = 'Street is required';
        if (!form.house) newErrors.house = 'House number is required';
        if (!addressVerified) newErrors.address = 'Please enter a valid address';
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            console.log('Form is valid, navigating to hostDinnerDetails with data:', { ...form, coords });
            navigate('/hostDinnerDetails', {
                state: {
                    addressData: {
                        country: form.country,
                        city: form.city,
                        neighborhood: form.neighborhood,
                        street: form.street,
                        house: form.house,
                        coords: coords
                    }
                }
            });
        }
    };

    useEffect(() => {
        console.log('Current map coordinates:', coords);
    }, [coords]);

    return (
        <div className="container-fluid min-vh-100 d-flex flex-column p-0" style={{ background: 'none' }}>
            <div className="flex-grow-1 d-flex justify-content-center align-items-center" style={{marginTop:'15vh', marginBottom:'5vh'}}>
                <div className="card shadow-lg p-4 rounded-4 w-100" style={{ maxWidth: 700, background: '#fff' }}>
                    <div className="d-flex align-items-center mb-3" style={{ justifyContent: 'flex-end' }}>
                        <span role="img" aria-label="house" style={{ fontSize: 32, marginRight: 12 }}>🏠</span>
                        <span className="host-dinner-link mb-0" style={{ fontWeight: 600, color: "#15316b", marginLeft: 8, textAlign: "right", fontSize: '1.2rem' }}>Host a Dinner</span>
                    </div>
                    <h2 className="mb-2" style={{ fontWeight: 'bold', fontSize: '1.3rem', textAlign: 'center' }}>
                        Set Your location
                    </h2>
                    <div className="mb-2" style={{ color: '#555', textAlign: 'center' }}>
                        Your address will only be shared with guests after you confirm their request.
                    </div>
                    <form onSubmit={handleSubmit} className="mb-4">
                        <div className="row g-3">
                            <div className="col-md-6 position-relative">
                                <label className="form-label">Country <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    className={`form-control${errors.country ? ' is-invalid' : ''}`}
                                    name="country"
                                    value={form.country}
                                    onChange={e => handleAutocomplete('country', e.target.value)}
                                    autoComplete="country"
                                    onFocus={() => setActiveField('country')}
                                />
                                {activeField === 'country' && suggestions.length > 0 && (
                                    <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, top: '100%' }}>
                                        {suggestions.map((s, idx) => (
                                            <li key={s.place_id || idx} className="list-group-item list-group-item-action" onClick={() => handleSuggestionClick(s)}>{s.name || s.display_name.split(',')[0].trim()}</li>
                                        ))}
                                    </ul>
                                )}
                                {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                            </div>
                            <div className="col-md-6 position-relative">
                                <label className="form-label">City <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    className={`form-control${errors.city ? ' is-invalid' : ''}`}
                                    name="city"
                                    value={form.city}
                                    onChange={e => handleAutocomplete('city', e.target.value)}
                                    autoComplete="address-level2"
                                    onFocus={() => setActiveField('city')}
                                />
                                {activeField === 'city' && suggestions.length > 0 && (
                                    <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, top: '100%' }}>
                                        {suggestions.map((s, idx) => (
                                            <li key={s.place_id || idx} className="list-group-item list-group-item-action" onClick={() => handleSuggestionClick(s)}>{s.name || s.display_name.split(',')[0].trim()}</li>
                                        ))}
                                    </ul>
                                )}
                                {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                            </div>
                            <div className="col-md-6 position-relative">
                                <label className="form-label">Neighborhood</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="neighborhood"
                                    value={form.neighborhood}
                                    onChange={e => handleAutocomplete('neighborhood', e.target.value)}
                                    autoComplete="address-level3"
                                    onFocus={() => setActiveField('neighborhood')}
                                />
                                {activeField === 'neighborhood' && suggestions.length > 0 && (
                                    <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, top: '100%' }}>
                                        {suggestions.map((s, idx) => (
                                            <li key={s.place_id || idx} className="list-group-item list-group-item-action" onClick={() => handleSuggestionClick(s)}>{s.name || s.display_name.split(',')[0].trim()}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="col-md-6 position-relative">
                                <label className="form-label">Street <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    className={`form-control${errors.street ? ' is-invalid' : ''}`}
                                    name="street"
                                    value={form.street}
                                    onChange={e => handleAutocomplete('street', e.target.value)}
                                    autoComplete="address-line1"
                                    onFocus={() => setActiveField('street')}
                                />
                                {activeField === 'street' && suggestions.length > 0 && (
                                    <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, top: '100%' }}>
                                        {suggestions.map((s, idx) => (
                                            <li key={s.place_id || idx} className="list-group-item list-group-item-action" onClick={() => handleSuggestionClick(s)}>{s.name || s.display_name.split(',')[0].trim()}</li>
                                        ))}
                                    </ul>
                                )}
                                {errors.street && <div className="invalid-feedback">{errors.street}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">House Number <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    className={`form-control${errors.house ? ' is-invalid' : ''}`}
                                    name="house"
                                    value={form.house}
                                    onChange={handleChange}
                                    autoComplete="address-line2"
                                />
                                {errors.house && <div className="invalid-feedback">{errors.house}</div>}
                            </div>
                        </div>
                        <div className="d-flex justify-content-center mt-4">
                            <button
                                type="submit"
                                className="btn btn-warning px-4 py-2 rounded-pill fw-bold text-white"
                                style={{ background: '#ffb97a', border: 'none', minWidth: 180 }}
                                disabled={!addressVerified}
                            >
                                All set!
                            </button>
                        </div>
                        {errors.address && <div className="text-danger text-center mt-2">{errors.address}</div>}
                    </form>
                    <div className="d-flex justify-content-center mt-2">
                        <div style={{ width: '100%', maxWidth: 400, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
                            <MapContainer
                                center={coords}
                                zoom={15}
                                style={{ height: '180px', width: '100%', borderRadius: '16px' }}
                                dragging={false}
                                scrollWheelZoom={false}
                                doubleClickZoom={false}
                                zoomControl={false}
                                attributionControl={false}
                                key={`${coords[0]}-${coords[1]}`}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <Marker position={coords} />
                            </MapContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}